"use client"

import { Fragment, useRef, useState, useEffect, useId } from "react"
import { useInView } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { AutoplayVideo } from "@/components/ui/autoplay-video"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { CtaBanner } from "@/components/ui/cta-banner"
import { aboutKeys as k } from "@/i18n/about-keys"
import { jobSlug, type JobListing } from "@/lib/cms"
import Link from "next/link"
import Image from "next/image"
import { useLocalePath } from "@/lib/use-localized-navigation"
import {
  ActionPlanIcon,
  CheckIcon,
  ChecklistIcon,
  ChevronDownIcon,
  ExerciseIcon,
  GoogleIcon,
  LifestyleIcon,
  LinkedInIcon,
  MapPinIcon,
  StarIcon,
} from "@/components/ui/icons"
import { TrustedPartnerIcon } from "@/components/ui/trusted-partner-icon"
import { blobMediaUrl } from "@/lib/blob-url"
import { splitIntoSentences } from "@/lib/split-paragraphs"
import type { ReactNode } from "react"
import {
  FaqAccordion,
  type FaqAccordionItem,
} from "@/components/ui/faq-accordion"
import {
  COMPANY_EMAIL,
  type AboutFacts,
  type AboutOptionalPath,
} from "@/lib/about-facts"
import {
  aboutCopyArgs,
  aboutVariantCopy,
  buildAboutKeyFacts,
  type KeyFactRow,
} from "@/lib/about-key-facts"
import {
  ANGEL_INVESTORS,
  INVESTOR_FUNDS,
  LEADERSHIP_FOUNDERS,
  MEDICAL_FOUNDERS,
  personDisplayName,
} from "@/lib/company"

/* ───────────────────── CountUp ───────────────────── */

/**
 * Counts up to `target` once the number scrolls into view.
 *
 * The first render (server HTML included) shows the real value, so crawlers,
 * no-JS readers and the markdown-less HTML all read "45%", not "0%". After
 * hydration the counter only rewinds to 0 if the number is still below the
 * fold, then animates back up when it scrolls in; a number already on screen,
 * or a visitor who prefers reduced motion, keeps the final value.
 */
function CountUp({ target }: { target: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [value, setValue] = useState(target)
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    const rect = el.getBoundingClientRect()
    const onScreen = rect.top < window.innerHeight && rect.bottom > 0
    if (onScreen) return
    setValue(0)
    setArmed(true)
  }, [])

  useEffect(() => {
    if (!armed || !isInView) return
    const duration = 1000
    let frame = 0
    const step = (timestamp: number, startTime?: number) => {
      const st = startTime ?? timestamp
      const progress = Math.min((timestamp - st) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 4)
      setValue(Math.round(eased * target))
      if (progress < 1) frame = requestAnimationFrame((t) => step(t, st))
    }
    frame = requestAnimationFrame((t) => step(t))
    return () => cancelAnimationFrame(frame)
  }, [armed, isInView, target])

  return <span ref={ref}>{value}</span>
}

/* ───────────────────── TeamPhoto ───────────────────── */

function TeamPhoto({ src, name }: { src?: string; name: string }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
  if (src)
    return (
      <Image src={src} alt={name} fill className="object-cover object-top" />
    )
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300">
      <span className="type-title-lg text-neutral-400">{initials}</span>
    </div>
  )
}

/* ───────────────────── CompanyLogo ───────────────────── */

const KNOWN_LOGOS: Record<string, { height: number; maxWidth: number }> = {
  "google.svg": { height: 12, maxWidth: 60 },
  "oxford.svg": { height: 12, maxWidth: 60 },
  "octorad.png": { height: 12, maxWidth: 60 },
  "kaia.svg": { height: 18, maxWidth: 90 },
  "coolblue.svg": { height: 12, maxWidth: 60 },
  "suitsupply.svg": { height: 12, maxWidth: 60 },
  "iqvia.svg": { height: 12, maxWidth: 60 },
  "uzh.svg": { height: 12, maxWidth: 60 },
  "eth.svg": { height: 12, maxWidth: 60 },
  "rtp-global.svg": { height: 12, maxWidth: 60 },
}

function CompanyLogo({ src, name }: { src: string; name: string }) {
  const filename = src.split("/").pop() || ""
  const logoConfig = KNOWN_LOGOS[filename]
  if (logoConfig) {
    return (
      <Image
        src={src}
        alt={name}
        width={logoConfig.maxWidth}
        height={logoConfig.height}
        className="w-auto object-contain object-left opacity-40 grayscale"
        style={{ height: logoConfig.height, maxWidth: logoConfig.maxWidth }}
      />
    )
  }
  return (
    <span className="type-body-sm font-medium tracking-wider text-neutral-300 uppercase">
      {name}
    </span>
  )
}

/* ───────────────────── KeyFacts ───────────────────── */

/**
 * One `<dl>` holding every company fact. The first tiles are always visible;
 * the rest are hidden with CSS (never unmounted) behind "All company
 * details", so the full list is in the server HTML for crawlers either way.
 */
function KeyFacts({ facts }: { facts: AboutFacts }) {
  const t = useTranslations("about")
  const tLocations = useTranslations("locations")
  const tTests = useTranslations("tests")
  const lp = useLocalePath()
  const [expanded, setExpanded] = useState(false)
  const extraId = useId()

  const rows = buildAboutKeyFacts({
    t,
    facts,
    locationsValue:
      facts.locationCount === null
        ? null
        : tLocations("locationCount", { count: facts.locationCount }),
    customersValue: tTests("customerCount"),
  })
  const primary = rows.filter((r) => r.primary)
  const extra = rows.filter((r) => !r.primary)

  const linkClass =
    "hover:text-aubergine-800 underline decoration-neutral-300 underline-offset-2 transition-colors"
  const renderValue = (row: KeyFactRow) => {
    if (row.links)
      return row.links.map((l, i) => (
        <span key={l.href}>
          {i > 0 && ", "}
          <a
            href={l.href}
            {...(l.href.startsWith("http")
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className={linkClass}
          >
            {l.label}
          </a>
        </span>
      ))
    if (row.note)
      return (
        <>
          <a
            href={row.href}
            target="_blank"
            rel="noopener noreferrer"
            className="type-title-lg hover:text-aubergine-800 transition-colors"
          >
            {row.value}
          </a>{" "}
          <span className="type-body-sm text-neutral-500">({row.note})</span>
        </>
      )
    if (!row.href) return row.value
    if (row.href.startsWith("/"))
      return (
        <Link href={lp(row.href)} className={linkClass}>
          {row.value}
        </Link>
      )
    return (
      <a
        href={row.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {row.value}
      </a>
    )
  }

  return (
    <section id="key-facts" className="scroll-mt-24 py-10 md:py-14">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <p className="type-label-lg text-neutral-400">{t(k.factsEyebrow)}</p>
        <h2 className="type-display-sm mt-4 text-neutral-900">
          {t(k.factsHeading)}
        </h2>
        <dl className="mt-10 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {primary.map((row) => (
            <div
              key={row.id}
              className="flex flex-col gap-2 rounded-[16px] bg-white p-4 md:p-5"
            >
              <dt className="type-label-md text-neutral-400">{row.label}</dt>
              <dd className="type-title-sm text-neutral-900">
                {renderValue(row)}
              </dd>
            </div>
          ))}
          {/* One <div> group of dt/dd pairs (valid inside <dl>), hidden
              with CSS until expanded so every row stays in the HTML. */}
          {extra.length > 0 && (
            <div
              id={extraId}
              className={`${expanded ? "grid" : "hidden"} col-span-2 grid-cols-1 gap-x-8 rounded-[16px] bg-white px-5 py-2 sm:grid-cols-[minmax(0,14rem)_1fr] lg:col-span-4`}
            >
              {extra.map((row, i) => (
                <Fragment key={row.id}>
                  <dt
                    className={`type-label-md pt-3 text-neutral-400 ${i > 0 ? "border-t border-neutral-100" : ""}`}
                  >
                    {row.label}
                  </dt>
                  <dd
                    className={`type-body-md pb-3 text-neutral-700 sm:pt-3 ${i > 0 ? "sm:border-t sm:border-neutral-100" : ""}`}
                  >
                    {renderValue(row)}
                  </dd>
                </Fragment>
              ))}
            </div>
          )}
        </dl>
        {extra.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={extraId}
            className={`${buttonVariants({ variant: "outline" })} mt-8`}
          >
            {expanded ? t(k.factsShowFewer) : t(k.factsShowAll)}
            <ChevronDownIcon
              className={`ml-1 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
    </section>
  )
}

/* ───────────────────── Props ───────────────────── */

interface AboutClientProps {
  /** CMS FAQs tagged `about`; the section is omitted when there are none. */
  faqs: FaqAccordionItem[]
  jobs: JobListing[]
  /** Market-dependent facts, read on the server (`getAboutFacts`). */
  facts: AboutFacts
}

/* ───────────────────── Component ───────────────────── */

/* Jobs — CMS is source of truth */

export function AboutClient({ jobs, facts, faqs }: AboutClientProps) {
  const t = useTranslations("about")
  const tCareers = useTranslations("careers")
  const lp = useLocalePath()
  const has = (path: AboutOptionalPath) => facts.availablePaths.includes(path)
  const tNav = useTranslations("nav")
  const variant = aboutVariantCopy(t, facts)
  const copyArgs = aboutCopyArgs(facts.intlLocale)
  const channelLink = (href: string) =>
    function ChannelLink(chunks: ReactNode) {
      return (
        <a
          href={href}
          {...(href.startsWith("http")
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
          className="hover:text-aubergine-800 font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 transition-colors"
        >
          {chunks}
        </a>
      )
    }

  const translateJobType = (type: string) =>
    type === "Full-time"
      ? tCareers("jobTypeFullTime")
      : type === "Part-time"
        ? tCareers("jobTypePartTime")
        : type

  return (
    <>
      {/* ── Hero ────────────────────────────────────────── */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden pt-[96px] pb-16 md:pt-[72px] md:pb-0">
        {/* Gradient fallback (always visible behind video) */}
        <div className="from-aubergine-900 absolute inset-0 bg-gradient-to-br via-[#1a1a2e] to-[#0f0f23]" />{" "}
        {/* ds-color-ignore: gradient stop */}
        {/* Video — desktop only */}
        <div className="absolute inset-0 hidden md:block">
          <AutoplayVideo
            src={blobMediaUrl("/videos/about-hero.mp4")}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        {/* Content */}
        <div className="relative z-10 mx-auto max-w-[1440px] px-4 text-center md:px-10">
          <div className="mb-6 flex justify-center">
            <a
              href="https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m16!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m5!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!16s%2Fg%2F11x1bl_tpp?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read Ahead Health reviews on Google"
              className="inline-flex items-center gap-1.5 rounded-full transition-opacity hover:opacity-80"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <StarIcon key={i} />
              ))}
              <GoogleIcon width={18} height={18} className="ml-0.5" />
            </a>
          </div>
          <h1 className="type-display-lg text-white">{t(k.heroHeadline)}</h1>

          {/* The one-sentence definition of the company sits directly under
              the H1, server-rendered: it is the line search engines and AI
              assistants quote when asked what Ahead Health is. */}
          <p className="type-body-xl mx-auto mt-6 max-w-3xl text-white/80">
            {variant.heroValueProp}
          </p>

          <div className="mx-auto mt-10 flex w-full max-w-md flex-col justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("approach")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="type-body-md inline-flex w-full cursor-pointer items-center justify-center rounded-full bg-white px-8 py-3 font-medium text-neutral-900 transition-all hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.04)] sm:w-auto"
            >
              {t(k.heroCta)}
            </button>
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("careers")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="type-body-md inline-flex w-full cursor-pointer items-center justify-center rounded-full border border-white/40 px-8 py-3 font-medium text-white transition-all hover:bg-white/10 sm:w-auto"
            >
              {t(k.heroOpenRoles)}
            </button>
          </div>
        </div>
      </section>

      {/* ── Problem / manifesto ─────────────────────────── */}
      <section className="pt-20 pb-10 md:pt-28 md:pb-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid gap-10 md:grid-cols-2 md:gap-20">
            <div>
              <p className="type-label-lg text-neutral-400">
                {t(k.problemLabel)}
              </p>
              <h2 className="type-display-sm mt-6 text-balance text-neutral-900">
                {splitIntoSentences(t(k.problemHeadline)).map((sentence, i) => (
                  <span key={i} className="inline text-balance md:block">
                    {sentence}{" "}
                  </span>
                ))}
              </h2>
              <p className="type-body-lg mt-6 text-neutral-600">
                {t(k.problemBody)}
              </p>
            </div>

            <div>
              <div className="ml-auto grid max-w-[560px] grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="type-body-lg bg-aubergine-900 flex min-h-[240px] flex-col justify-end rounded-[32px] p-6 text-white sm:aspect-square sm:p-8">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    <CountUp target={45} />%
                  </p>
                  <p>{t(k.problemStatDesc)}</p>
                  <p className="type-body-sm mt-3 text-white/60">
                    {t(k.problemStatSource)}
                  </p>
                </div>

                <div className="type-body-lg bg-light-green-400 flex min-h-[240px] flex-col justify-end rounded-[32px] p-6 text-neutral-900 sm:aspect-square sm:p-8">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    <CountUp target={90} />%
                  </p>
                  <p>{t(k.problemStat2Text)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What Ahead Health does — service tiles ─────── */}
      <section id="approach" className="scroll-mt-24 py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-center text-neutral-400">
            {t(k.approachLabel)}
          </p>
          <h2 className="type-display-md mt-4 text-center text-neutral-900">
            {t(k.servicesHeading)}
          </h2>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
            {t(k.approachHeadline)}
          </p>

          {(() => {
            const SERVICES: {
              path: AboutOptionalPath
              image: string
              title: string
              desc: string
              /** An MRI tile: shown only behind the MRI gate (`offersMri`). */
              mri?: boolean
            }[] = [
              {
                path: "/pricing",
                mri: true,
                image: blobMediaUrl(
                  "/images/homepage/service-mri-machine.jpeg"
                ),
                title: t(k.serviceCheckupTitle),
                desc: t(k.serviceCheckupDesc),
              },
              {
                path: "/tests/full-body-mri",
                mri: true,
                image: blobMediaUrl("/images/homepage/service-mri.jpeg"),
                title: t(k.serviceMriTitle),
                desc: t(k.serviceMriDesc),
              },
              ...(facts.bloodTest
                ? [
                    {
                      path: facts.bloodTest.path,
                      image: blobMediaUrl(
                        "/images/homepage/service-blood.jpeg"
                      ),
                      title: tNav(facts.bloodTest.labelKey),
                      desc: t(k.serviceBloodDesc),
                    },
                  ]
                : []),
              {
                path: "/report",
                image: blobMediaUrl("/images/how-it-works/hero-app.png"),
                title: t(k.servicePlanTitle),
                desc: t(k.servicePlanDesc),
              },
            ]
            const visible = SERVICES.filter(
              (s) => has(s.path) && (!s.mri || facts.offersMri)
            )

            return (
              <ul
                className={`mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 ${
                  visible.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"
                }`}
              >
                {visible.map((service) => (
                  <li key={service.path}>
                    <Link
                      href={lp(service.path)}
                      className="group flex h-full flex-col rounded-[16px] bg-white p-3 transition-shadow hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)]"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[12px]">
                        <Image
                          src={service.image}
                          alt=""
                          fill
                          sizes={`(min-width: 1024px) ${
                            visible.length === 3 ? "33vw" : "25vw"
                          }, (min-width: 640px) 50vw, 100vw`}
                          className="object-cover"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-3 pt-6">
                        <h3 className="type-title-md flex items-center gap-2 text-neutral-900">
                          {service.title}
                          <ArrowRightIcon
                            size={18}
                            className="text-neutral-400 transition-transform duration-200 group-hover:translate-x-1"
                          />
                        </h3>
                        <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                          {service.desc}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )
          })()}
        </div>
      </section>

      {/* ── What makes Ahead Health different ──────────── */}
      <section className="bg-background py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">
            {t(k.differentEyebrow)}
          </p>
          <h2 className="type-display-sm mt-4 text-neutral-900">
            {t(k.differentHeading)}
          </h2>
          <p className="type-body-lg mt-4 max-w-2xl text-neutral-600">
            {t(k.differentIntro)}
          </p>

          {(() => {
            const DIFFERENTIATORS = [
              {
                title: t(k.diff1Title),
                desc: t(k.diff1Desc),
                ahead: t(k.diff1Ahead),
                standard: t(k.diff1Standard),
                // Blood-panel claim: only where the market sells a blood test
                // of its own (not Austria, AHD-14017).
                blood: true,
              },
              {
                title: t(k.diff2Title),
                desc: t(k.diff2Desc),
                ahead: t(k.diff2Ahead),
                standard: t(k.diff2Standard),
                // Blood-panel claim: only where the market sells a blood test
                // of its own (not Austria, AHD-14017).
                blood: true,
              },
              {
                title: t(k.diff3Title),
                desc: t(k.diff3Desc),
                ahead: t(k.diff3Ahead),
                standard: t(k.diff3Standard),
                // Behind the MRI gate (lib/about-facts-server.ts): DE, NL and
                // AT sell no MRI yet, so the comparison and its "1 in 5" stat
                // would advertise an offer the market doesn't have.
                mri: true,
              },
              {
                title: t(k.diff4Title),
                desc: t(k.diff4Desc),
                ahead: t(k.diff4Ahead),
                standard: t(k.diff4Standard),
              },
              {
                title: t(k.diff5Title),
                desc: t(k.diff5Desc),
                ahead: t(k.diff5Ahead),
                standard: t(k.diff5Standard),
              },
            ].filter(
              (d) =>
                (!("mri" in d) || facts.offersMri) &&
                (!("blood" in d) || facts.bloodTest !== null)
            )

            // Rows of three on desktop, two on tablet: centre a short last
            // row instead of leaving it hanging on the left.
            const n = DIFFERENTIATORS.length
            const placement = (i: number) =>
              [
                "md:col-span-3 lg:col-span-2",
                n % 3 === 2 && i === n - 2 ? "lg:col-start-2" : "",
                n % 3 === 1 && i === n - 1 ? "lg:col-start-3" : "",
                n % 2 === 1 && i === n - 1 ? "md:col-span-6" : "",
              ].join(" ")

            return (
              <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-6">
                {DIFFERENTIATORS.map((d, i) => (
                  <div
                    key={d.title}
                    className={`flex h-full flex-col rounded-[16px] bg-white p-6 ${placement(i)}`}
                  >
                    <h3 className="type-title-md text-neutral-900">
                      {d.title}
                    </h3>
                    <p className="type-body-md mt-3 text-neutral-600">
                      {d.desc}
                    </p>
                    <dl className="mt-auto divide-y divide-neutral-100 pt-6">
                      <div className="flex items-start justify-between gap-4 py-2">
                        <dt className="type-body-sm text-neutral-500">
                          {t(k.diffAheadLabel)}
                        </dt>
                        <dd className="type-body-sm inline-flex items-center gap-1.5 text-right font-medium text-neutral-900">
                          <CheckIcon
                            width={16}
                            height={16}
                            className="text-deep-green-500 flex-shrink-0"
                          />
                          {d.ahead}
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 py-2">
                        <dt className="type-body-sm text-neutral-500">
                          {t(k.diffStandardLabel)}
                        </dt>
                        <dd className="type-body-sm text-right text-neutral-400">
                          {d.standard}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Who uses Ahead Health ───────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:px-10">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.whoEyebrow)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.whoHeading)}
            </h2>
            <p className="type-body-lg mt-4 max-w-md text-neutral-600">
              {t(k.whoIntro)}
            </p>
          </div>

          {(() => {
            const SEGMENTS: {
              title: string
              desc: string
              path?: AboutOptionalPath
            }[] = [
              {
                title: t(k.who1Title),
                desc: variant.who1Desc,
                path: facts.offersMri
                  ? "/tests/full-body-mri"
                  : facts.bloodTest?.path,
              },
              {
                title: t(k.who2Title),
                desc: t(k.who2Desc),
                path: facts.bloodTest?.path,
              },
              {
                title: t(k.who3Title),
                desc: t(k.who3Desc),
                path: "/heart-health",
              },
              { title: t(k.who4Title), desc: t(k.who4Desc) },
              {
                title: t(k.who5Title),
                desc: t(k.who5Desc),
                path: "/female-hormones",
              },
              {
                title: t(k.who6Title),
                desc: t(k.who6Desc),
                path: "/metabolism",
              },
              { title: t(k.who7Title), desc: t(k.who7Desc), path: "/teams" },
              { title: t(k.who8Title), desc: t(k.who8Desc), path: "/contact" },
            ]

            return (
              <ul className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
                {SEGMENTS.map((segment) => {
                  const href =
                    segment.path && has(segment.path)
                      ? lp(segment.path)
                      : undefined
                  const body = (
                    <>
                      <h3 className="type-title-sm group-hover:text-aubergine-800 inline-flex items-center gap-1.5 text-neutral-900 transition-colors">
                        {segment.title}
                        {href && (
                          <ArrowRightIcon
                            size={16}
                            className="text-neutral-400 transition-transform duration-200 group-hover:translate-x-1"
                          />
                        )}
                      </h3>
                      <p className="type-body-md mt-2 text-neutral-600">
                        {segment.desc}
                      </p>
                    </>
                  )
                  return (
                    <li
                      key={segment.title}
                      className="border-t border-neutral-200 pt-4"
                    >
                      {href ? (
                        <Link href={href} className="group block">
                          {body}
                        </Link>
                      ) : (
                        body
                      )}
                    </li>
                  )
                })}
              </ul>
            )
          })()}
        </div>
      </section>

      {/* ── Team — 4 tiers ──────────────────────────────── */}
      <section
        id="team"
        className="bg-background scroll-mt-24 px-6 py-10 md:py-14"
      >
        <div className="mx-auto max-w-5xl">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.teamLabel)}</p>
          </div>

          <h2 className="type-display-sm mt-4 text-neutral-900">
            {t(k.teamHeading)}
          </h2>

          <p className="type-body-lg mt-4 max-w-3xl text-neutral-600">
            {t(k.teamOrigin, {
              city: copyArgs.city,
              year: copyArgs.year,
              leadership: copyArgs.leadershipNames,
              radiologists: copyArgs.radiologists,
            })}
          </p>

          <p className="type-title-md mt-8 text-neutral-900">
            {t(k.teamHeadline)}
          </p>
          <p className="type-body-md mt-1 max-w-3xl text-neutral-500">
            {t(k.teamSubtitle)}
          </p>

          {/* Tier 1 — Leadership */}
          {(() => {
            const CARDS: Record<
              string,
              {
                title: string
                photo: string
                logos: { src: string; name: string }[]
              }
            > = {
              "nick-lenten": {
                title: t(k.leadershipNickTitle),
                photo: blobMediaUrl("/images/about/nick-lenten.png"),
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/google.svg"),
                    name: "Google",
                  },
                  {
                    src: blobMediaUrl("/images/logos/coolblue.svg"),
                    name: "Coolblue",
                  },
                  {
                    src: blobMediaUrl("/images/logos/suitsupply.svg"),
                    name: "Suitsupply",
                  },
                  { src: "/images/logos/everyman.svg", name: "Everyman" },
                ],
              },
              "sven-jaeschke": {
                title: t(k.leadershipSvenTitle),
                photo: blobMediaUrl("/images/about/sven-jaeschke.png"),
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/oxford.svg"),
                    name: "Oxford",
                  },
                  { src: "/images/logos/deepspin.svg", name: "DeepSpin" },
                  {
                    src: blobMediaUrl("/images/logos/iqvia.svg"),
                    name: "IQVIA",
                  },
                ],
              },
              "dennis-hermann": {
                title: t(k.leadershipDennisTitle),
                photo: blobMediaUrl("/images/about/dennis-hermann.png"),
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/kaia.svg"),
                    name: "Kaia Health",
                  },
                ],
              },
              "vega-vinsensius": {
                title: t(k.leadershipVegaTitle),
                photo: blobMediaUrl("/images/about/vega-vinsensius.png"),
                logos: [
                  {
                    src: blobMediaUrl("/images/logos/eth.svg"),
                    name: "ETH Zurich",
                  },
                  {
                    src: blobMediaUrl("/images/logos/google.svg"),
                    name: "Google",
                  },
                ],
              },
            }
            const LEADERSHIP = LEADERSHIP_FOUNDERS.map((p) => ({
              name: personDisplayName(p),
              role: p.role,
              linkedin: p.linkedin,
              ...CARDS[p.id],
            }))

            return (
              <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
                {LEADERSHIP.map((person) => (
                  <div key={person.name} className="h-full">
                    <div className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <TeamPhoto src={person.photo} name={person.name} />
                        <span className="type-body-sm absolute top-3 left-3 rounded-full bg-white px-3 py-1 font-semibold tracking-wider text-neutral-900 uppercase">
                          {person.role}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="type-title-sm text-neutral-900">
                            {person.name}
                          </p>
                          {person.linkedin && (
                            <a
                              href={person.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`${person.name} on LinkedIn`}
                              className="flex-shrink-0 text-neutral-400 transition-colors hover:text-[#0A66C2]" // ds-color-ignore: third-party / brand logo colour
                            >
                              <LinkedInIcon width={14} height={14} />
                            </a>
                          )}
                        </div>
                        <p className="type-body-sm mt-0.5 text-neutral-500">
                          {person.title}
                        </p>
                        <div className="mt-auto flex h-[28px] flex-wrap items-center gap-2 overflow-hidden pt-3">
                          {person.logos.map((logo) => (
                            <CompanyLogo
                              key={logo.name}
                              src={logo.src}
                              name={logo.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Tier 2 — Medical co-founders */}
          {(() => {
            const PHOTOS: Record<string, string> = {
              "olivio-donati": blobMediaUrl("/images/about/olivio-donati.jpg"),
              "michael-fischer": blobMediaUrl(
                "/images/about/michael-fischer.jpg"
              ),
            }
            const MEDICAL_PARTNERS = MEDICAL_FOUNDERS.map((p) => ({
              name: personDisplayName(p),
              role: t(k.medicalPartnerRole),
              title: t(k.medicalPartnerTitle),
              photo: PHOTOS[p.id],
              logos: [
                { src: blobMediaUrl("/images/logos/uzh.svg"), name: "UZH" },
                {
                  src: blobMediaUrl("/images/logos/octorad.png"),
                  name: "Octorad",
                },
                { src: "/images/logos/hirslanden.svg", name: "Hirslanden" },
              ],
            }))

            return (
              <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
                {MEDICAL_PARTNERS.map((person, i) => (
                  <div
                    key={person.name}
                    className={i === 0 ? "lg:col-start-2" : undefined}
                  >
                    <div className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <TeamPhoto src={person.photo} name={person.name} />
                        {/* nowrap: the badge is shrink-to-fit over the photo,
                            so once German hyphenation applies site-wide
                            (AHD-10495) the lower min-content width of a
                            soft-hyphenated compound like "Mitgründer" would
                            break it across lines instead of leaving it as one
                            pill. */}
                        <span className="type-label-sm absolute top-3 left-3 rounded-full bg-[#3D1C51] px-3 py-1 font-semibold tracking-wider whitespace-nowrap text-white uppercase">
                          {person.role}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <p className="type-title-sm text-neutral-900">
                          {person.name}
                        </p>
                        <p className="type-body-sm mt-0.5 text-neutral-500">
                          {person.title}
                        </p>
                        <div className="mt-auto flex h-[28px] flex-wrap items-center gap-2 overflow-hidden pt-3">
                          {person.logos.map((logo) => (
                            <CompanyLogo
                              key={logo.name}
                              src={logo.src}
                              name={logo.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Tier 3 — Advisory board */}
          {(() => {
            const ADVISORS = [
              {
                name: "Dr. med. Anna Erat, PhD",
                credential: t(k.advisorCredential1),
                photo: blobMediaUrl("/images/doctors/erat.jpeg"),
              },
              {
                name: "Prof. Dr. med. Edouard Battegay",
                credential: t(k.advisorCredential4),
                photo:
                  "https://rc1mdjsplvzsekgk.public.blob.vercel-storage.com/battegay.jpg",
              },
              {
                name: "Prof. Dr. Benedikt Wiestler",
                credential: t(k.advisorCredential2),
                photo: blobMediaUrl("/images/doctors/wiestler.jpeg"),
              },
              {
                name: "Dr. Jacob J. Visser",
                credential: t(k.advisorCredential3),
                photo: blobMediaUrl("/images/doctors/visser.jpg"),
              },
            ]

            return (
              <div id="advisory-board" className="mt-14 scroll-mt-24">
                <div>
                  <p className="type-label-md mb-6 text-neutral-400">
                    {t(k.advisoryBoardLabel)}
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:gap-12">
                    {ADVISORS.map((advisor) => (
                      <div
                        key={advisor.name}
                        className="flex items-center gap-3"
                      >
                        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full">
                          <Image
                            src={advisor.photo}
                            alt={advisor.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div>
                          <p className="type-title-sm text-neutral-900">
                            {advisor.name}
                          </p>
                          <p className="type-body-sm text-neutral-500">
                            {advisor.credential}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Tier 4 — Wider team */}
          {(() => {
            const WIDER_TEAM = [
              {
                name: "Katrine",
                fullName: "Katrine Singer",
                role: t(k.widerTeamClinicalOps),
                photo: blobMediaUrl("/images/about/team/katrine.jpg"),
              },
              {
                name: "Agata",
                fullName: "Dr. Agata Jagoda",
                role: t(k.widerTeamGpPrevention),
                photo: blobMediaUrl("/images/about/team/agata.webp"),
              },
              {
                name: "Hadrien",
                fullName: "Dr. Hadrien Komaromi",
                role: t(k.widerTeamGpPreventionM),
                photo: blobMediaUrl("/images/about/team/hadrien.webp"),
              },
              {
                name: "Laurent",
                fullName: "Laurent Meyer",
                role: t(k.widerTeamSoftwareEngineer),
                photo: blobMediaUrl("/images/about/team/laurent.jpg"),
              },
              {
                name: "David",
                fullName: "David Gaynor",
                role: t(k.widerTeamSoftwareEngineer),
                photo: blobMediaUrl("/images/about/team/david.jpg"),
              },
              {
                name: "Eugene",
                fullName: "Eugene Chorny",
                role: t(k.widerTeamProductDesigner),
                photo: blobMediaUrl("/images/about/team/eugene.jpg"),
              },
              {
                name: "Lydia",
                fullName: "Lydia",
                role: t(k.widerTeamHeadOfGrowth),
                photo: blobMediaUrl("/images/about/team/lydia.jpg"),
              },
              {
                name: "Laura",
                fullName: "Laura Leoty",
                role: t(k.widerTeamCustomerSuccess),
                photo: blobMediaUrl("/images/about/team/laura.jpg"),
              },
              {
                name: "Ferdinand",
                fullName: "Ferdinand Skaugerum",
                role: t(k.widerTeamChiefOfStaff),
                photo: blobMediaUrl("/images/about/team/ferdinand.jpg"),
              },
              {
                name: "Michèle",
                fullName: "Michèle Noten",
                role: t(k.widerTeamHeadOfMarketing),
                photo: blobMediaUrl("/images/about/team/michele.jpg"),
              },
              {
                name: "Grégoire",
                fullName: "Grégoire",
                role: t(k.widerTeamFoundingSeniorProductEngineer),
                photo: blobMediaUrl("/images/about/team/gregoire.jpg"),
              },
              {
                name: "Samantha",
                fullName: "Samantha",
                role: t(k.widerTeamClinicalOpsHealthData),
                photo: blobMediaUrl("/images/about/team/samantha.jpg"),
              },
              {
                name: "Andres",
                fullName: "Andres Lanzos",
                role: t(k.widerTeamFoundingAiMlEngineer),
                photo: blobMediaUrl("/images/about/team/andres.jpg"),
              },
            ]

            return (
              <div className="mt-14">
                <div>
                  <p className="type-label-md mb-6 text-neutral-400">
                    {t(k.widerTeamLabel)}
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
                    {WIDER_TEAM.map((member) => (
                      <div
                        key={member.name}
                        className="group relative -mx-2 flex cursor-default items-center gap-3 rounded-xl px-2 py-1.5 transition-colors duration-[250ms] hover:bg-white"
                      >
                        <Image
                          src={member.photo}
                          alt={member.fullName}
                          width={40}
                          height={40}
                          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                        />
                        <div className="min-w-0">
                          <span className="type-body-sm group-hover:text-aubergine-900 block text-neutral-600 transition-colors duration-[250ms]">
                            {member.name}
                          </span>
                          {/* Wraps, like the name above it: in a two-column
                            grid on a phone this span is ~109px wide, and
                            "Huisarts, preventiespecialist" (151px) or
                            "Allgemeinmedizinerin, Präventionsexpertin" used to
                            be cut mid-word. The qualification is the point of
                            the line, so it gets a second line instead. */}
                          <span className="type-body-sm block text-neutral-400">
                            {member.role}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Investors ───────────────────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg pl-8 text-neutral-400 md:pl-12">
              {t(k.investorsLabel)}
            </p>
          </div>

          <div>
            <h2 className="type-display-sm mt-4 pl-8 text-neutral-900 md:pl-12">
              {t(k.investorsHeadline)}
            </h2>
          </div>

          <div>
            <div className="mt-10 rounded-[16px] bg-white p-8 md:p-12">
              {/* Lead investor, then the other funds */}
              {INVESTOR_FUNDS.slice(0, 1).map((fund) => (
                <Image
                  key={fund.name}
                  src={blobMediaUrl(fund.logo)}
                  alt={fund.name}
                  width={320}
                  height={64}
                  className="h-16 w-auto object-contain object-left"
                />
              ))}

              <div className="mt-6 flex items-center gap-10">
                {INVESTOR_FUNDS.slice(1).map((fund) => (
                  <Image
                    key={fund.name}
                    src={blobMediaUrl(fund.logo)}
                    alt={fund.name}
                    width={100}
                    height={24}
                    className="h-6 w-auto object-contain object-left"
                  />
                ))}
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-neutral-100" />

              {/* Angels label */}
              <p className="type-label-md mb-4 text-neutral-400">
                {t(k.investorsAngelsLabel)}
              </p>

              {/* Angels grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ANGEL_INVESTORS.map((angel) => (
                  <div key={angel.name}>
                    <div>
                      <p className="type-title-sm text-neutral-900">
                        {angel.name}
                      </p>
                      <p className="type-body-sm text-neutral-500">
                        {angel.affiliationKey
                          ? t(angel.affiliationKey)
                          : angel.affiliation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How Ahead Health works ──────────────────────── */}
      <section className="bg-background py-10 md:py-14">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:px-10">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.howEyebrow)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.howHeading)}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-[16px] bg-white p-6">
              <h3 className="type-title-md text-neutral-900">
                {t(k.howCheckupTitle)}
              </h3>
              <ol className="type-body-md mt-4 list-decimal space-y-3 pl-5 text-neutral-600 marker:font-semibold marker:text-neutral-900">
                <li>{t(k.howCheckup1)}</li>
                <li>{variant.howCheckup2}</li>
                <li>{variant.howCheckup3}</li>
                <li>{t(k.howCheckup4)}</li>
                <li>{t(k.howCheckup5)}</li>
              </ol>
            </div>

            <div className="rounded-[16px] bg-white p-6">
              <h3 className="type-title-md text-neutral-900">
                {t(k.howContactTitle)}
              </h3>
              <ul className="type-body-md mt-4 list-disc space-y-3 pl-5 text-neutral-600 marker:text-neutral-400">
                {facts.whatsappDigits && (
                  <li>
                    {t.rich(k.howContactWhatsapp, {
                      link: channelLink(
                        `https://wa.me/${facts.whatsappDigits}`
                      ),
                    })}
                  </li>
                )}
                {facts.phone && (
                  <li>
                    {t.rich(k.howContactPhone, {
                      phone: facts.phone,
                      link: channelLink(
                        `tel:${facts.phone.replace(/\s/g, "")}`
                      ),
                    })}
                  </li>
                )}
                <li>
                  {t.rich(k.howContactEmail, {
                    email: COMPANY_EMAIL,
                    link: channelLink(`mailto:${COMPANY_EMAIL}`),
                  })}
                </li>
                {has("/free-call") && (
                  <li>
                    {t.rich(k.howContactCall, {
                      link: (chunks) => (
                        <Link
                          href={lp("/free-call")}
                          className="hover:text-aubergine-800 font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 transition-colors"
                        >
                          {chunks}
                        </Link>
                      ),
                    })}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Key facts ───────────────────────────────────── */}
      <KeyFacts facts={facts} />

      {/* ── Frequently asked questions ──────────────────── */}
      {faqs.length > 0 && (
        <section className="py-10 md:py-14">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 md:grid-cols-[1fr_2fr] md:px-10">
            <div>
              <p className="type-label-lg text-neutral-400">
                {t(k.faqEyebrow)}
              </p>
              <h2 className="type-display-sm mt-4 text-neutral-900">
                {t(k.faqHeading)}
              </h2>
            </div>
            {/* Answers stay in the DOM while collapsed (FaqAccordion), so the
              server HTML carries every answer; the FAQPage JSON-LD is
              emitted by page.tsx from the same CMS items. */}
            <FaqAccordion questionHeadingLevel="h3" items={faqs} />
          </div>
        </section>
      )}

      {/* ── Values — 6 cards, next to careers (speaks to candidates) ── */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-neutral-400">{t(k.valuesLabel)}</p>
          </div>

          <div>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.valuesHeading)}
            </h2>
          </div>

          {(() => {
            const VALUES = [
              {
                icon: (
                  <TrustedPartnerIcon size={22} className="text-neutral-900" />
                ),
                name: t(k.valueTrustedSidekickName),
                desc: t(k.valueTrustedSidekickDesc),
              },
              {
                icon: <LifestyleIcon size={22} className="text-neutral-900" />,
                name: t(k.valueAlwaysThereName),
                desc: t(k.valueAlwaysThereDesc),
              },
              {
                icon: <ChecklistIcon size={22} className="text-neutral-900" />,
                name: t(k.valueBetterEveryDayName),
                desc: t(k.valueBetterEveryDayDesc),
              },
              {
                icon: <ActionPlanIcon size={22} className="text-neutral-900" />,
                name: t(k.valueActionablyProactiveName),
                desc: t(k.valueActionablyProactiveDesc),
              },
              {
                icon: <ExerciseIcon size={22} className="text-neutral-900" />,
                name: t(k.valueGrittyName),
                desc: t(k.valueGrittyDesc),
              },
              {
                icon: (
                  <MapPinIcon
                    width={22}
                    height={22}
                    className="text-neutral-900"
                  />
                ),
                name: t(k.valueThinkBigName),
                desc: t(k.valueThinkBigDesc),
              },
            ]

            return (
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-8 md:gap-x-10 lg:grid-cols-3">
                {VALUES.map((value) => (
                  <div key={value.name}>
                    {value.icon}
                    <h3 className="type-title-sm mt-3 text-neutral-900">
                      {value.name}
                    </h3>
                    <p className="type-body-sm md:type-body-md mt-2 text-neutral-600">
                      {value.desc}
                    </p>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      </section>

      {/* ── Careers ─────────────────────────────────────── */}
      <section
        id="careers"
        className="bg-background scroll-mt-24 px-6 py-10 md:py-14"
      >
        <div className="mx-auto max-w-5xl">
          <div>
            <p className="type-label-lg text-center text-neutral-400">
              {t(k.careersLabel)}
            </p>
          </div>

          <div>
            <h2 className="type-display-md mt-4 text-center text-neutral-900">
              {t(k.careersHeadline)}
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {jobs.map((job) => (
              <div key={job.title}>
                <Link
                  href={lp("/careers/" + jobSlug(job))}
                  className="group flex flex-col gap-4 rounded-[16px] bg-white p-6 transition-shadow hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="type-title-md text-neutral-900">
                      {job.title}
                    </p>
                    <p className="type-body-sm mt-1 text-neutral-500">
                      {translateJobType(job.type)} · {job.location}
                    </p>
                  </div>
                  <span
                    className={buttonVariants({ variant: "outline" })}
                    aria-hidden="true"
                  >
                    {t(k.careersSeeRole)}
                  </span>
                </Link>
              </div>
            ))}

            {/* Open application */}
            <div>
              <a
                href="mailto:nick@aheadhealth.com"
                className="flex flex-col gap-4 rounded-[16px] border border-dashed border-neutral-300 p-6 transition-colors hover:border-neutral-400 hover:bg-white/50 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="type-title-md text-neutral-900">
                    {t(k.careersOpenAppTitle)}
                  </p>
                  <p className="mt-1 text-sm text-neutral-500">
                    {t(k.careersOpenAppDesc)}
                  </p>
                </div>
                <span
                  className={buttonVariants({ variant: "outline" })}
                  aria-hidden="true"
                >
                  {t(k.careersOpenAppCta)}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ─────────────────────────────────── */}
      <div>
        <CtaBanner
          title={t(k.closingHeadline)}
          imageSrc={blobMediaUrl("/images/misc/advisory-bg.png")}
          primaryCta={{ label: t(k.closingCta), href: lp("/how-it-works") }}
          className="!min-h-[280px] md:!min-h-[400px]"
          overlayClassName="hidden"
        />
      </div>
    </>
  )
}
